<template>
  <v-container class="my-5">
    <h2 class="subheading grey--text">{{$t('pending_surveys')}}</h2>
    <v-row class="mt-2 mb-0">
      <v-col cols="12" sm="4">
        <v-select
          v-model="filterType"
          :items="filterOptions"
          label="Filtrar por tipo"
          dense
          outlined
          hide-details
        ></v-select>
      </v-col>
    </v-row>
    <!-- Surveys List -->
    <v-layout class="my-4" align-center justify-center fill-height>
      <v-container fluid>
        <v-row dense>
          <v-col v-for="survey in filteredSurveys" :key="survey.id" cols="12" md="6">
            <v-card class="mb-3 elevation-4" :color="survey.is_voting ? '#A7C7E7' : '#E8F5E9'">
              <v-card-title>{{ survey.title }}</v-card-title>
              <v-card-subtitle class="pt-3 pb-1 grey--text text--darken-2">
                {{ survey.description }}
              </v-card-subtitle>
              <v-card-text>
                <div class="mt-2">
                  <v-icon small class="mr-1">mdi-calendar</v-icon>
                  <strong>{{$t('survey_end_date')}}:</strong> {{ formatDate(survey.end_date) }}
                </div>
                <div class="mt-2">
                  <v-icon small class="mr-1">mdi-help-box-multiple</v-icon>
                  <strong>{{ survey.questions ? survey.questions.length : 0 }}</strong> {{$t('survey_questions')}}
                </div>
              </v-card-text>
              <v-card-actions class="pb-3 px-4">
                <v-tooltip bottom v-if="survey.answered">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon color="green" v-bind="attrs" v-on="on" @click="viewResults(survey)">
                      <v-icon>mdi-chart-bar</v-icon>
                    </v-btn>
                  </template>
                  <span>{{$t('survey_results')}}</span>
                </v-tooltip>
                <v-spacer></v-spacer>
                <v-btn v-if="!survey.answered" color="primary" @click="openSurveyDialog(survey)">
                  <v-icon left>mdi-pencil</v-icon>
                  {{$t('answer_survey')}}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="filteredSurveys.length === 0 && !loadingData">
          <v-col cols="12" class="text-center mt-5">
            <v-icon size="64" color="grey lighten-1">mdi-ballot-outline</v-icon>
            <h3 class="grey--text mt-3">{{$t('msg_no_pending_surveys')}}</h3>
          </v-col>
        </v-row>
        <v-row v-if="loadingData">
          <v-col cols="12" class="text-center mt-5">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-col>
        </v-row>
      </v-container>
    </v-layout>
    <!-- Dialog to Answer Survey -->
    <v-dialog v-model="dialogAnswer" max-width="700" scrollable persistent>
      <v-card v-if="selectedSurvey" class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ selectedSurvey.title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn class="align-self-end" color="red" icon @click="dialogAnswer = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-5">
          <div class="mb-4 text-justify subtitle-1">
            {{ selectedSurvey.description }}
          </div>
          <v-divider class="mb-5"></v-divider>
          <v-form ref="form" v-model="validForm">
            <div v-for="(question, index) in selectedSurvey.questions" :key="question.id" class="mb-6">
              <h3 class="font-weight-medium mb-3">{{ index + 1 }}. {{ question.text }}</h3>
              <div v-if="question.question_type === 'SINGLE_CHOICE'" class="ml-4">
                <v-radio-group v-model="answersData[question.id]" :rules="[v => !!v || 'Selecciona una opción']" column>
                  <v-radio
                    v-for="option in question.options"
                    :key="option.id"
                    :label="option.text"
                    :value="option.id"
                    color="primary"
                  ></v-radio>
                </v-radio-group>
              </div>
              <div v-if="question.question_type === 'MULTIPLE_CHOICE'" class="ml-4">
                <v-checkbox
                  v-for="option in question.options"
                  :key="option.id"
                  v-model="answersData[question.id]"
                  :label="option.text"
                  :value="option.id"
                  color="primary"
                  hide-details
                  class="mt-1"
                ></v-checkbox>
              </div>
              <div v-if="question.question_type === 'TEXT'" class="ml-4">
                <v-textarea
                  v-model="answersData[question.id]"
                  outlined
                  auto-grow
                  rows="2"
                  :label="language === 'es' ? 'Tu respuesta' : 'Your answer'"
                  :rules="[v => !!v || 'Este campo es requerido']"
                ></v-textarea>
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="dialogAnswer = false">{{$t('cancelar')}}</v-btn>
          <v-btn color="primary" :disabled="!validForm" :loading="loadingSubmit" @click="submitSurvey()">
            <v-icon left>mdi-send</v-icon>
            {{$t('submit_answers')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Dialog to View Results -->
    <v-dialog v-model="dialogResults" max-width="800" scrollable>
      <v-card class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{$t('survey_results')}} - {{ selectedSurvey.title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn class="align-self-end" color="red" icon @click="dialogResults = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <div v-if="resultsData && resultsData.results">
            <div v-for="(res, qText) in resultsData.results" :key="qText" class="mb-4">
              <h3>{{ qText }}</h3>
              <v-simple-table v-if="!Array.isArray(res)">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">{{$t('options')}}</th>
                      <th class="text-left">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(count, optionText) in res" :key="optionText">
                      <td>{{ optionText }}</td>
                      <td>{{ count }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-list v-else dense>
                <v-list-item v-for="(textResp, idx) in res" :key="idx">
                  <v-list-item-content>
                    <v-list-item-title>• {{ textResp }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <p v-if="res.length === 0" class="grey--text">No hay respuestas</p>
              </v-list>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogResults = false">{{$t('cerrar')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'SurveyResident',
  data () {
    return {
      surveys: [],
      filterType: 'all',
      loadingData: false,
      loadingSubmit: false,
      dialogAnswer: false,
      selectedSurvey: {},
      answersData: {},
      validForm: false,
      dialogResults: false,
      resultsData: {}
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    ...mapState(['language']),
    filterOptions () {
      return [
        { text: 'Todos', value: 'all' },
        { text: this.$t('surveys') || 'Encuestas', value: 'survey' },
        { text: this.$t('voting') || 'Votaciones', value: 'voting' }
      ]
    },
    filteredSurveys () {
      if (this.filterType === 'survey') {
        return this.surveys.filter(s => !s.is_voting)
      } else if (this.filterType === 'voting') {
        return this.surveys.filter(s => s.is_voting)
      }
      return this.surveys
    }
  },
  created () {
    this.getSurveys()
  },
  methods: {
    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString()
    },
    getSurveys () {
      this.loadingData = true
      const config = {
        method: 'get',
        url: 'https://residencial-services.intrare.app/resident/surveys/',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          this.surveys = response.data.filter(survey => survey.questions && survey.questions.length > 0)
          this.loadingData = false
        })
        .catch(error => {
          console.error(error)
          this.loadingData = false
        })
    },
    viewResults (survey) {
      console.log(survey)
      this.selectedSurvey = survey
      this.resultsData = {}
      const config = {
        method: 'get',
        url: `https://residencial-services.intrare.app/admin/surveys/${survey.id}/results/`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          this.resultsData = response.data
          this.dialogResults = true
        })
        .catch(error => {
          console.error(error)
        })
    },
    openSurveyDialog (survey) {
      this.selectedSurvey = survey
      this.answersData = {}
      // Initialize answers
      if (survey.questions) {
        survey.questions.forEach(q => {
          if (q.question_type === 'MULTIPLE_CHOICE') {
            this.$set(this.answersData, q.id, [])
          } else {
            this.$set(this.answersData, q.id, null)
          }
        })
      }
      this.dialogAnswer = true
      // Reset form validation if possible
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    submitSurvey () {
      if (this.$refs.form.validate()) {
        this.loadingSubmit = true
        let answersPayload = []
        for (const [idQuestion, answerValue] of Object.entries(this.answersData)) {
          let questionObj = this.selectedSurvey.questions.find(q => q.id === idQuestion)
          if (questionObj) {
            if (questionObj.question_type === 'SINGLE_CHOICE') {
              answersPayload.push({
                idQuestion: idQuestion,
                idQuestionOption: answerValue
              })
            } else if (questionObj.question_type === 'MULTIPLE_CHOICE') {
              if (answerValue && answerValue.length > 0) {
                answerValue.forEach(val => {
                  answersPayload.push({
                    idQuestion: idQuestion,
                    idQuestionOption: val
                  })
                })
              }
            } else if (questionObj.question_type === 'TEXT') {
              answersPayload.push({
                idQuestion: idQuestion,
                text_answer: answerValue
              })
            }
          }
        }
        const config = {
          method: 'post',
          url: `https://residencial-services.intrare.app/resident/surveys/${this.selectedSurvey.id}/submit/`,
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': this.language,
            'authorization': 'Token ' + store.getters.getToken
          },
          data: {
            answers: answersPayload
          }
        }
        axios(config)
          .then(response => {
            this.loadingSubmit = false
            this.dialogAnswer = false
            this.$store.commit('snackbar_activate_true')
            this.$store.commit('snackbar_change_message', this.$t('msg_survey_submitted_successfully'))
            this.getSurveys() // Refresh list, the answered survey should disappear
          })
          .catch(error => {
            this.loadingSubmit = false
            console.error(error)
            if (error.response && error.response.data && error.response.data.error) {
              // handle error
              alert(error.response.data.error)
            }
          })
      }
    }
  }
}
</script>

<style scoped>
.subtitle-1 {
  line-height: 1.6;
}
</style>
